import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import {
  Box,
  Chip,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { getUsersLight } from "../../api/user.api";
import { useAuth } from "../../context/auth.context";
import {
  closeOrder,
  committOrderEntry,
  payOrderEntry,
  publishOrder,
  removeOrder,
  removeOrderEntry,
} from "../../api/order.api";
import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DeleteIcon from "@mui/icons-material/Delete";
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import CommitOrderModal from "../modals/commitOrder.modal";
import { useSnackbar } from "../../context/snackbar.context";

const OrderCard = ({ order, onAdd }) => {
  const [users, setUsers] = useState([]);
  const [openCommittModal, setOpenCommittModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState();
  const auth = useAuth();
  const showSnackbar = useSnackbar();

  useEffect(() => {
    getUsersLight(order.entries.map((entry) => entry.userId))
      .then((response) => setUsers(response.data))
      .catch((error) => console.log(error));
  }, [order]);

  const handleRemoveEntry = (orderEntryId) => {
    removeOrderEntry(order.id, orderEntryId)
      .then(() => showSnackbar("Entry removed!", "success"))
      .catch((error) => showSnackbar("Failed removing Order!", "error"));
  };

  const handleCommit = (orderEntry) => {
    setSelectedEntry(orderEntry);
    setOpenCommittModal(true);
  };

  const handleCommitClose = () => {
    setSelectedEntry({});
    setOpenCommittModal(false);
  };

  const handleCommitSubmit = (formData) => {
    committOrderEntry(order.id, selectedEntry.id, {
      ...selectedEntry,
      price: formData.price,
    })
      .then(() => {
        showSnackbar("Set entry comitted!", "success")
        setSelectedEntry({ undefined });
        setOpenCommittModal(false);
      })
      .catch((error) => console.log(error));
  };

  const handlePay = (orderEntryId) => {
    payOrderEntry(order.id, orderEntryId)
      .then(() => showSnackbar("Set entry payed!", "success"))
      .catch((error) => console.log(error));
  };

  const handleRemove = () => {
    removeOrder(order.id)
      .then(() => showSnackbar("Order removed!", "success"))
      .catch((error) => console.log(error));
  };

  const handleClose = () => {
    closeOrder(order.id)
      .then(() => refresh())
      .catch((error) => console.log(error));
  };

  const handlePublish = () => {
    publishOrder(order.id)
      .then(() => refresh())
      .catch((error) => console.log(error));
  };

  const getName = (id) => {
    if(users) {
      return users.length > 0 ? users.find((user) => user.id === id)?.fullName : "";
    } else {
      return "";
    }
  };

  const getColor = (status) => {
    if (status === "Open") {
      return "secondary";
    }
    if (status === "Ordered") {
      return "success";
    }
  }

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minWidth: 200,
      }}
    >
      <CardContent>
        <Typography variant="h5">{order.name}</Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            paddingTop: 1,
            paddingBottom: 1,
          }}
        >
          <Chip label={order.status} variant="outlined" size="small" color={getColor(order.status)} />
          <Link href={order.hyperlink} rel="noreferrer" sx={{ ml: 2 }}>
            Menu List
          </Link>
          {order.paypal && order.paypal.length > 0 && (
            <Link
              sx={{ color: "white", fontSize: "25px", ml: 2 }}
              href={order.paypal}
              rel="noreferrer"
            >
              <RequestQuoteIcon />
            </Link>)}
        </Box>

        {order && order.entries.length > 0 && (
          <div>
            <Divider />

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>C|P</TableCell>
                    {auth && auth.user && auth.user.role === "Admin" && (
                      <TableCell>Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.entries.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography>{getName(row.userId)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{row.item}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {row.price} {row.price && "€"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.committed && !row.payed && <CheckIcon />}
                        {row.committed && row.payed && <DoneAllIcon />}
                      </TableCell>
                      {auth && auth.user && auth.user.role === "Admin" && (
                        <TableCell>
                          <Stack spacing={1} direction="row">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleRemoveEntry(row.id)}
                              startIcon={<DeleteIcon />}
                            >
                              Delete
                            </Button>
                            {!row.committed && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleCommit(row)}
                              >
                                Commit
                              </Button>
                            )}

                            {row.committed && !row.payed && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handlePay(row.id)}
                              >
                                Set Payed
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  <TableRow>
                  <TableCell>SUM</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell>{order.entries.map(entry => entry.price ?? 0).reduce((acc, curr) => acc + curr, 0)} €</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
      </CardContent>
      <CardActions>
        {order.status === "Open" && (
          <Button onClick={onAdd} size="small">
            Add Order
          </Button>
        )}
        {auth &&
          auth.user &&
          auth.user.role === "Admin" &&
          order.status === "Open" && (
            <Button onClick={handlePublish} size="small">
              Publish
            </Button>
          )}
        {auth &&
          auth.user &&
          auth.user.role === "Admin" &&
          order.status === "Ordered" && (
            <Button onClick={handleClose} size="small">
              Close
            </Button>
          )}

        {auth &&
          auth.user &&
          auth.user.role === "Admin" &&
          order.status === "Closed" && (
            <Button onClick={handleRemove} size="small">
              Remove
            </Button>
          )}
      </CardActions>

      <CommitOrderModal
        aria-modal
        open={openCommittModal}
        handleClose={handleCommitClose}
        handleSubmit={handleCommitSubmit}
      />
    </Card>
  );
};

export default OrderCard;
